import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, FileText, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/integrations/supabase/client";

export function ManualUploadTab() {
  const { toast } = useToast();
  const { orgId, familyId } = useTenant();
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // Upload to storage
        const fileName = `${Date.now()}-${file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("documents")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Create document record
        const { error: docError } = await supabase.from("documents").insert({
          org_id: orgId,
          family_id: familyId || null,
          client_id: "00000000-0000-0000-0000-000000000000", // Will need proper client_id
          type: "unknown",
          source: "manual_upload",
          storage_url: uploadData.path,
          sha256: "", // Could calculate hash if needed
          processing_status: "pending",
        });

        if (docError) throw docError;
      }

      toast({
        title: "Upload successful",
        description: `${files.length} document(s) uploaded and queued for processing`,
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Upload failed",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="space-y-3 text-sm">
            <h3 className="font-semibold">Upload Documents Directly</h3>
            <p>Supported formats: PDF, CSV, XLSX</p>
            <p className="text-muted-foreground">
              Documents uploaded here will be automatically analyzed to extract holdings, transactions, and other data.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-primary/10 p-4 mb-4">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Upload Documents</h3>
            <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
              Drag and drop files here, or click to browse
            </p>
            <div className="relative">
              <input
                type="file"
                multiple
                accept=".pdf,.csv,.xlsx"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <Button disabled={uploading}>
                {uploading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Select Files
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-4">What happens after upload?</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div>
                <p className="font-medium">Automatic Classification</p>
                <p className="text-sm text-muted-foreground">
                  Documents are automatically categorized (statements, transactions, etc.)
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div>
                <p className="font-medium">Data Extraction</p>
                <p className="text-sm text-muted-foreground">
                  Holdings, transactions, and other data are extracted using AI
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5" />
              <div>
                <p className="font-medium">Review & Approval</p>
                <p className="text-sm text-muted-foreground">
                  You'll be notified to review extracted data before it's applied
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
